import { Chip } from "@mui/material";
import LockPersonIcon from "@mui/icons-material/LockPerson";
import { useAuth } from "react-oidc-context";
import { useTranslation } from "i18n-fe";
import { useScoreContext } from "../context/ScoreContext";

// #1289 — the one escape hatch a kiosk needs. Scribe deliberately hides sign-out
// and every other route out of the scoring flow, which is right until the
// session itself dies: then the app is queueing work it can never deliver, and
// the only recovery (sign in again) is the thing that's hidden. So this appears
// ONLY after a 401 whose silent renew also failed (`authExpired`) — a merely
// expired token renews behind the scenes and the scribe sees nothing at all.
//
// #1562 — a 403 is deliberately excluded: it means the token is fine and this
// principal is refused (permission, wrong org, revoked entitlement), which no
// renew/sign-out/sign-in cycle can fix. Routing it through `authExpired` used
// to latch this chip forever with a "tap to sign in" promise that could never
// be kept. A 403 surfaces instead via the sync-issue badge (see sync.js
// classifyError/isSurfacedError), which doesn't lie about the remedy.
//
// Nothing is lost by tapping it: the outbox is IndexedDB-backed and survives the
// Cognito round-trip, so the queued marks drain once the new token lands. And
// nothing redirects on its own — a hard redirect mid-ride would yank the scribe
// off the sheet they're writing on, so the navigation is behind their tap.
//
// Colour: `error` semantic, never MUI `primary` — primary is dark grey against
// grey.900 in this app's dark theme and the chip reads as invisible (see the
// sibling chips in SessionHeader for the same note).
export default function SessionExpiredChip() {
  const { authExpired } = useScoreContext();
  const { signinRedirect } = useAuth();
  const { t } = useTranslation("score");

  if (!authExpired) return null;

  return (
    <Chip
      data-testid="session-expired"
      size="small"
      icon={<LockPersonIcon />}
      label={t("status.sessionExpired")}
      color="error"
      variant="filled"
      clickable
      onClick={() => void signinRedirect()}
      sx={{
        fontWeight: 600,
        // Filled `error` gives white-on-red in both themes; pin the icon to the
        // same contrast text so it doesn't fall back to the muted default.
        "& .MuiChip-icon": { color: "error.contrastText" },
      }}
    />
  );
}
